import { HttpErrorResponse } from '@angular/common/http';
import {
    AfterViewChecked,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { SortEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { filter, take } from 'rxjs/operators';
import { STANDARD_FIELDS } from '../../../../../shared/fields/standard-fields';
import * as fromRoot from '../../../core/store';
import * as layoutActions from '../../../core/store/layout/layout.actions';
import { Division, Field, StorageBox, WarehouseLocation, WarehouseSubLocation } from '../../../core/store/models';
import { storageBoxSelectors } from '../../../core/store/selectors';
import * as storageBoxActions from '../../../core/store/storage-boxes/storage-box.actions';
import { HeaderField, TableFilterObject } from '../../../core/store/types';
import { ActionStatus } from '../../../core/store/types';
import { buildPDropdownOptions } from '../../../core/store/utils';
import { STORAGE_BOX_MODAL_TYPE, StorageBoxModalComponent, StorageBoxModalData } from '../../../division/storage/storage-box-modal/storage-box-modal.component';
import { PDropdownOption } from '../../interfaces/p-dropdown-options';
import { ColumnFilterService } from '../../services/column-filter.service';
import { ConfirmModalComponent, ConfirmModalData } from '../confirm-modal/confirm-modal.component';

@Component({
    selector: 'ah-storage-box-table',
    templateUrl: './storage-box-table.component.html',
    styleUrls: ['./storage-box-table.component.scss'],
    standalone: false
})
export class StorageBoxTableComponent implements OnInit, AfterViewChecked, OnChanges {
    @Input() columnFilters?: TableFilterObject = {};
    @Input() columnFilterService: ColumnFilterService;
    @Input() canEdit: boolean;
    @Input() division: Division;
    @Input() fields: Field[];
    @Input() isLoading: boolean;
    @Input() storageBoxes: StorageBox[];
    @Input() storageBoxField: Field;
    @Input() warehouseId: number;
    @Input() warehouseLocationEnts: { [warehouseLocationId: number]: WarehouseLocation } = {};
    @Input() warehouseSubLocationEnts: { [warehouseSubLocationId: number]: WarehouseSubLocation } = {};

    @Output() delete = new EventEmitter();
    @Output() filterTableColumns = new EventEmitter();
    @Output() sort = new EventEmitter();

    @ViewChild('tt', { static: true }) turboTable: Table;

    columns: HeaderField[] = [
        { canonical_name: 'name', name: 'Box Name' },
        { canonical_name: 'description', name: 'Description' },
        { canonical_name: 'space', name: 'Space?' }
    ];
    filterValues: string[] = [];
    rowsPerPageOptions: number[] = [10, 25, 50, 100];
    rowsPerPage: number = 25;
    searchParam = '';
    spaceOptions = [{ label: 'All', value: '' }, { label: 'Has Space', value: 'true' }];
    warehouseLocationOptions: PDropdownOption[];
    warehouseSubLocationOptions: PDropdownOption[];

    constructor(public dialog: MatDialog, private store: Store<fromRoot.State>) { }

    ngOnInit() {
        const storageBoxFields: HeaderField[] = this.fields.map(f => ({
            canonical_name: f.canonical_name,
            name: f.canonical_name === STANDARD_FIELDS.barcode.canonical_name ? 'Storage Box Barcode' : f.name
        }));
        this.columns.splice(2, 0, ...storageBoxFields);
        this.setupColumnFilters();
        this.turboTable.globalFilterFields = this.columns.map(c => c.canonical_name).filter(n => n !== STANDARD_FIELDS.warehouseLocation.canonical_name && n !== STANDARD_FIELDS.warehouseSubLocation.canonical_name);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.warehouseLocationEnts || changes.warehouseSubLocationEnts || changes.warehouseId) {
            this.setupWarehouseLocationOptions();
        }
        if (changes.columnFilters) {
            this.setupColumnFilters();
        }

        if (changes.warehouseId) {
            this.turboTable.reset();
        }
    }

    ngAfterViewChecked() {
        if (this.turboTable !== undefined && this.columnFilterService) {
            this.columnFilterService.setCustomFilterConstraints();
        }
    }

    onColumnFilter(value: string | number[] | boolean, field: Field) {
        if (typeof value === 'string') {
            value = value.replace(/^\s+/g, '').replace(/\s\s+/g, ' ');
            this.filterValues[field.canonical_name] = value;
        }
        if (field.canonical_name === 'space') {
            this.turboTable.filter(value === null ? null : String(value), field.canonical_name, 'equals');
        } else if (field.canonical_name === STANDARD_FIELDS.warehouseLocation.canonical_name || field.canonical_name === STANDARD_FIELDS.warehouseSubLocation.canonical_name) {
            this.turboTable.filter(value, field.canonical_name, 'in');
        } else {
            this.turboTable.filter(value, field.canonical_name, 'contains');
        }
    }

    onRowClick(event: MouseEvent, storageBox: StorageBox) {
        if (this.canEdit) {
            this.openEditModal(storageBox);
        } else {
            this.store.dispatch(new layoutActions.ErrorAction({
                error: new HttpErrorResponse({
                    error: new Error('You do not have permission to edit assets in this project.')
                })
            }));
        }
    }

    onSort(sortObj: SortEvent) {
        this.sort.emit(sortObj);
    }

    onTableFilter(event) {
        this.filterTableColumns.emit(event.filters);
    }

    openCreateModal() {
        const data: StorageBoxModalData = {
            action: STORAGE_BOX_MODAL_TYPE.CREATE,
            division: this.division,
            warehouse_id: this.warehouseId
        };
        this.dialog.open(StorageBoxModalComponent, { data }).afterClosed().pipe(take(1)).subscribe(newStorageBox => {
            if (newStorageBox) {
                this.store.dispatch(new storageBoxActions.AddAction({ storageBox: newStorageBox }));
                this.store.select(storageBoxSelectors.getStorageBoxSaving).pipe(filter(s => s !== ActionStatus.Loading), take(1)).subscribe(status => {
                    if (status === ActionStatus.Complete) {
                        const message = `${this.storageBoxField.name} created.`;
                        this.store.dispatch(new layoutActions.InfoAction({ message }));
                    }
                });
            }
        });
    }

    openEditModal(storageBox: StorageBox) {
        const data: StorageBoxModalData = {
            action: STORAGE_BOX_MODAL_TYPE.EDIT,
            division: this.division,
            warehouse_id: this.warehouseId,
            storageBox
        };
        this.dialog.open(StorageBoxModalComponent, { data }).afterClosed().pipe(take(1)).subscribe(newStorageBox => {
            if (newStorageBox) {
                const confirmData: ConfirmModalData = {
                    title: `Move ${this.storageBoxField.name}`,
                    message: `This will update the storage location of all assets in this ${this.storageBoxField.name}`,
                    confirmButton: 'Confirm',
                };
                this.dialog.open(ConfirmModalComponent, { data: confirmData }).afterClosed().pipe(take(1)).subscribe(confirm => {
                    if (confirm) {
                        this.store.dispatch(new storageBoxActions.UpdateAction({ storageBox: newStorageBox }));
                        this.store.select(storageBoxSelectors.getStorageBoxSaving).pipe(filter(s => s !== ActionStatus.Loading), take(1)).subscribe(status => {
                            if (status === ActionStatus.Complete) {
                                const message = `${this.storageBoxField.name} and its contents have been moved.`;
                                this.store.dispatch(new layoutActions.InfoAction({ message }));
                            }
                        });
                    }
                });
            }
        });
    }

    getObjLength(obj: {}) {
        return Object.keys(obj).length;
    }

    onDelete(event, storageBox: StorageBox) {
        event.stopPropagation();
        this.delete.emit(storageBox);
    }

    private setupColumnFilters() {
        this.turboTable.filters = this.columnFilters;
        this.turboTable.globalFilterFields = this.columns.map(c => c.canonical_name).filter(n => n !== STANDARD_FIELDS.warehouseLocation.canonical_name && n !== STANDARD_FIELDS.warehouseSubLocation.canonical_name);
        this.filterValues = [];

        if (this.columnFilters.global) {
            this.filterValues['global'] = this.columnFilters.global.value;
        }

        Object.keys(this.columnFilters).forEach(fil => {
            const selectedField = this.columns.find(sel => sel.canonical_name === fil);
            if (selectedField) {
                const value = this.columnFilters[fil].value;
                switch (selectedField.canonical_name) {
                    case STANDARD_FIELDS.warehouseLocation.canonical_name: {
                        this.filterValues[fil] = value.join(',');
                        break;
                    }
                    case STANDARD_FIELDS.warehouseSubLocation.canonical_name: {
                        this.filterValues[fil] = value.join(',');
                        break;
                    }
                    default: {
                        this.filterValues[fil] = value;
                    }
                }
            }
        });
    }

    private setupWarehouseLocationOptions() {
        const warehouseLocations = [];
        for (const key in this.warehouseLocationEnts) {
            if (this.warehouseLocationEnts[key] && this.warehouseLocationEnts[key].warehouse_id === this.warehouseId) {
                warehouseLocations.push(this.warehouseLocationEnts[key]);
            }
        }
        this.warehouseLocationOptions = buildPDropdownOptions(warehouseLocations);
        const warehouseLocationIds = this.warehouseLocationOptions.map(o => o.value);
        const warehouseSubLocations = [];
        for (const key in this.warehouseSubLocationEnts) {
            if (this.warehouseSubLocationEnts[key] && warehouseLocationIds.includes(this.warehouseSubLocationEnts[key].warehouse_location_id)) {
                warehouseSubLocations.push(this.warehouseSubLocationEnts[key]);
            }
        }
        this.warehouseSubLocationOptions = buildPDropdownOptions(warehouseSubLocations);
    }

    showPaginator(items) {
        return items && items.length && items.length > this.rowsPerPage;
    }
}
